import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Users, Tv, DollarSign, History } from 'lucide-react';
import SparklineChart from './SparklineChart';
import Tooltip from './Tooltip';
import DataBadge from './DataBadge';
import HistoryModal from './HistoryModal';
import UZBEK_NAMES from '../constants/uzbekNames';

function fmt(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1e9) return (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return String(n);
}

export default function CategoryCard({ cat, index }) {
  const nav = useNavigate();
  const [showHistory, setShowHistory] = useState(false);
  const growth = cat.monthlyGrowth || 0;
  const isUp = growth >= 0;
  const displayName = UZBEK_NAMES[cat.name] || cat.name;

  return (
    <motion.div
      className="cat-card"
      style={{ '--cat-color': cat.color }}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.07, ease: 'easeOut' }}
      whileHover={{ y: -8, scale: 1.02, transition: { type: 'spring', stiffness: 340, damping: 22 } }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="cat-card-bar" style={{ background: `linear-gradient(135deg, ${cat.color}, ${darken(cat.color)})` }} />

      <div className="cat-card-body">
        <div className="cat-card-header">
          <h3 className="cat-name">{displayName}</h3>
          <Tooltip content={`So'nggi 30 kunda: ${isUp ? '+' : ''}${growth}% o'sish`}>
            <span className={`growth-badge ${isUp ? 'up' : 'down'}`}>
              {isUp ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
              {isUp ? '+' : ''}{growth}%
            </span>
          </Tooltip>
        </div>

        <div className="cat-stats">
          <Tooltip content={`Jami obunachi: ${fmt(cat.subscriberCount)} — O'tgan oyga nisbatan ${isUp ? '+' : ''}${growth}%`}>
            <div className="stat-item">
              <Users size={14} />
              <span>{fmt(cat.subscriberCount)}<DataBadge isReal={cat.isReal} /></span>
              <small>obunachi</small>
            </div>
          </Tooltip>

          <Tooltip content={`Faol kanallar soni: ${fmt(cat.channelCount)}`}>
            <div className="stat-item">
              <Tv size={14} />
              <span>{fmt(cat.channelCount)}</span>
              <small>kanal</small>
            </div>
          </Tooltip>

          <Tooltip content={`Har 1000 ko'rish uchun: $${cat.cpmMin} — $${cat.cpmMax} oralig'ida. Bu taxminiy qiymat.`}>
            <div className="stat-item">
              <DollarSign size={14} />
              <span>${cat.cpmMin}–${cat.cpmMax}<DataBadge isReal={false} label="~CPM" /></span>
              <small>CPM</small>
            </div>
          </Tooltip>
        </div>

        <div className="cat-sparkline">
          <SparklineChart data={cat.trendData} color={cat.color} />
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
          <button
            className="cat-btn"
            style={{ '--btn-color': cat.color, flex: 1 }}
            onClick={() => nav(`/category/${cat.id}`)}
          >
            Batafsil ko'rish →
          </button>
          <Tooltip content="Tarixiy ma'lumotni ko'rish">
            <button
              onClick={e => { e.stopPropagation(); setShowHistory(true); }}
              style={{
                flexShrink: 0,
                width: 38, height: 38,
                borderRadius: 10,
                border: `1.5px solid ${cat.color}55`,
                background: `${cat.color}12`,
                color: cat.color,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.2s, transform 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = `${cat.color}28`; e.currentTarget.style.transform = 'scale(1.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = `${cat.color}12`; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <History size={16} />
            </button>
          </Tooltip>
        </div>
      </div>

      {showHistory && (
        <HistoryModal
          catId={cat.id}
          catName={cat.name}
          catColor={cat.color}
          onClose={() => setShowHistory(false)}
        />
      )}
    </motion.div>
  );
}

function darken(hex) {
  if (!hex) return '#000';
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, (n >> 16) - 60);
  const g = Math.max(0, ((n >> 8) & 0xff) - 60);
  const b = Math.max(0, (n & 0xff) - 60);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}
